// src/lib/guided/explain.ts
//
// Phase 3.3 of docs/guided-shopping_plan.md: turns the answers that actually
// picked a resolved outcome into a visitor-facing explanation (research
// §3.4: "knowledge-based recommenders ... can explain which constraint
// failed"). Visitor-facing, unlike grade.ts's owner-only `reasons` (the
// grade itself never reaches the visitor, plan 3.6) -- this module never
// reads or mentions the grade, and the polite-no case surfaces the owner's
// own `notFit.text` verbatim rather than a sentence this module invents.
//
// Walks the same offer-survivor narrowing `resolveOutcome` (outcomes.ts)
// does, including the synthetic "need" criterion (`compile.ts`'s
// `buildNeedCriterion`) when there is more than one offer, so "which
// answers decided this" agrees with whichever outcome was actually reached:
// a criterion counts as deciding only when its answer removed at least one
// offer from contention, the identical offer-survivor rule `order.ts`
// already defines (`survivorsForBucket`).
import type { FunnelConfig, FunnelCriterion } from "./config";
import {
  NOT_SURE_YET_OPTION,
  PREFER_TO_DISCUSS_OPTION,
  buildNeedCriterion,
  effectiveOfferFit,
} from "./compile";
import { survivorsForBucket } from "./order";
import { resolveOutcome, POLITE_NO_OUTCOME_ID } from "./outcomes";

export interface ExplainResult {
  /** "Because you said X and Y, we suggest Z." or the owner's own notFit text. */
  headline: string;
  /** At most `MAX_EXPLAIN_REASONS` answer labels, in question order. */
  reasons: string[];
}

/** Plan 3.3's acceptance line: "at most two reasons". */
export const MAX_EXPLAIN_REASONS = 2;

function isHedgeValue(value: string): boolean {
  return (
    value === String(NOT_SURE_YET_OPTION.value) ||
    value === String(PREFER_TO_DISCUSS_OPTION.value)
  );
}

function bucketLabel(criterion: FunnelCriterion, value: string): string {
  return (
    criterion.buckets.find((bucket) => bucket.id === value)?.label ?? value
  );
}

/**
 * The answer labels, in question order, whose answer actually removed at
 * least one offer from contention -- the same offer-survivor walk
 * `resolveOutcome` runs, so this agrees with whichever outcome was reached.
 * Stops as soon as `MAX_EXPLAIN_REASONS` are found: a later criterion may
 * also have decided something, but the plan caps what is shown, not what
 * is computed.
 */
function decidingAnswerLabels(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): string[] {
  const offerFit = effectiveOfferFit(config);
  let remaining = config.offers.map((offer) => offer.id);
  const needCriterion =
    config.offers.length > 1 ? buildNeedCriterion(config.offers) : null;
  const criteria: FunnelCriterion[] = [
    ...(needCriterion ? [needCriterion] : []),
    ...config.criteria,
  ];

  const labels: string[] = [];
  for (const criterion of criteria) {
    if (labels.length >= MAX_EXPLAIN_REASONS) break;
    const value = answers[criterion.id];
    if (typeof value !== "string" || value === "" || isHedgeValue(value)) {
      continue;
    }
    const survivors = survivorsForBucket(
      criterion.id,
      value,
      remaining,
      offerFit,
    );
    if (survivors.length < remaining.length) {
      labels.push(bucketLabel(criterion, value));
    }
    remaining = survivors;
  }
  return labels;
}

/**
 * Plan 3.3: a visitor-facing explanation of the outcome `answers` reaches.
 * A matched offer explains as `Because you said "X" and "Y", we suggest
 * "Z".`; the polite no returns the owner's own `config.notFit.text`
 * unchanged, so the wording is always what the owner wrote for exactly
 * this case, never a sentence built from grading vocabulary.
 */
export function explainOutcome(
  config: FunnelConfig,
  answers: Record<string, unknown>,
): ExplainResult {
  const outcome = resolveOutcome(config, answers);
  const reasons = decidingAnswerLabels(config, answers);

  if (outcome.id === POLITE_NO_OUTCOME_ID) {
    return { headline: config.notFit.text, reasons };
  }

  const quotedReasons = reasons.map((reason) => `"${reason}"`);
  const headline =
    quotedReasons.length === 0
      ? `We suggest "${outcome.title}".`
      : `Because you said ${quotedReasons.join(" and ")}, we suggest "${outcome.title}".`;

  return { headline, reasons };
}
